// DoorGlassTypesSection.jsx
// Phase 2: Door Glass Types Section - Updated with Windows Glass Types style

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NumericField } from '@/components/NumericField';
import { getNumericFieldError } from '@/utils/numericValidation';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Plus, Trash2, Edit2, Eye, Loader2 } from 'lucide-react';

// Common glass thicknesses for select dropdown
const COMMON_GLASS_THICKNESSES = [
  { value: 0.125, label: '1/8" (3mm)' },
  { value: 0.1875, label: '3/16" (5mm)' },
  { value: 0.25, label: '1/4" (6mm)' },
  { value: 0.375, label: '3/8" (10mm)' },
  { value: 0.5, label: '1/2" (12mm)' },
  { value: 0.625, label: '5/8" (16mm)' },
  { value: 0.75, label: '3/4" (19mm)' },
  { value: 1, label: '1" (25mm)' },
];

// Glass features/specifications
const GLASS_FEATURES = [
  'Tempered', 'Laminated', 'Low-E Coated', 'Insulated', 
  'Heat Soaked', 'IG', 'Argon Filled', 'Frosted', 'Digital Printed'
];

export const DoorGlassTypesSection = ({
  // Data
  filteredDoorGlassTypes,
  selectedDoorGlassTypeIds,
  expandedDoorGlassTypeIds,
  canEditCSIItems,
  canManageProject,
  operationLoading,
  // Dialog state
  showDoorGlassTypeDialog,
  editingDoorGlassType,
  newDoorGlassType,
  doorGridSizeRef,
  // Handlers
  onSelectAll,
  onConfirmBulkDelete,
  onToggleSelection,
  onDialogOpenChange,
  onFormChange,
  onFormSubmit,
  onEdit,
  onDelete,
  onToggleExpand,
}) => {
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const numericErrors = [
    getNumericFieldError(newDoorGlassType.warrantyYears, { integer: true, gtZero: true, label: 'Warranty' }),
    newDoorGlassType.hasGrid
      ? getNumericFieldError(newDoorGlassType.gridSize, { required: true, min: 0, allowFraction: true, label: 'Grid size' })
      : null,
  ];
  const handleSubmit = (e) => {
    setSubmitAttempted(true);
    if (numericErrors.some(Boolean)) {
      e.preventDefault();
      return;
    }
    onFormSubmit(e);
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Glass Types</h2>
        <div className="flex gap-2">
          {canEditCSIItems && filteredDoorGlassTypes.length > 0 && (
            <>
              <Button 
                variant="outline"
                onClick={onSelectAll}
              >
                {selectedDoorGlassTypeIds.length === filteredDoorGlassTypes.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button 
                onClick={onConfirmBulkDelete}
                disabled={selectedDoorGlassTypeIds.length === 0}
                className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedDoorGlassTypeIds.length})
              </Button>
            </>
          )}
          {canEditCSIItems && (
            <Dialog open={showDoorGlassTypeDialog} onOpenChange={(open) => { setSubmitAttempted(false); onDialogOpenChange(open); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-door-glass-type-btn" disabled={operationLoading}>
                  {operationLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  Add Glass Type
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingDoorGlassType ? 'Edit Glass Type' : 'Add Glass Type'}</DialogTitle>
                  <DialogDescription>
                    Define glass specifications for door glass lites
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Type (e.g., GT1, GT2) *</Label>
                      <Input
                        placeholder="e.g., GT1, GT2, GT3"
                        value={newDoorGlassType.name}
                        onChange={(e) => onFormChange({ ...newDoorGlassType, name: e.target.value })}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Manufacturer</Label>
                      <Input
                        placeholder="e.g., Guardian, PPG, Cardinal"
                        value={newDoorGlassType.manufacturer || ''}
                        onChange={(e) => onFormChange({ ...newDoorGlassType, manufacturer: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Input
                        placeholder="e.g., SunGuard, Solarban 70XL"
                        value={newDoorGlassType.model || ''}
                        onChange={(e) => onFormChange({ ...newDoorGlassType, model: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Thickness (in)</Label>
                      <Select 
                        value={newDoorGlassType.glassThickness?.toString() || ''} 
                        onValueChange={(val) => onFormChange({...newDoorGlassType, glassThickness: parseFloat(val)})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select thickness" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMMON_GLASS_THICKNESSES.map((t) => (
                            <SelectItem key={t.value} value={t.value.toString()}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2 col-span-2">
                      <Label>Build</Label>
                      <Input
                        placeholder="e.g., 1/4 + 1/2 Air + 1/4"
                        value={newDoorGlassType.glassBuild || ''}
                        onChange={(e) => onFormChange({ ...newDoorGlassType, glassBuild: e.target.value })}
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Features</Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border rounded-md">
                      {GLASS_FEATURES.map((feature) => (
                        <label
                          key={feature}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <Checkbox
                            checked={(newDoorGlassType.features || []).includes(feature)}
                            onCheckedChange={(checked) => {
                              const currentFeatures = newDoorGlassType.features || [];
                              if (checked) {
                                onFormChange({
                                  ...newDoorGlassType,
                                  features: [...currentFeatures, feature]
                                });
                              } else {
                                onFormChange({
                                  ...newDoorGlassType,
                                  features: currentFeatures.filter(f => f !== feature)
                                });
                              }
                            }}
                          />
                          <span className="text-sm font-normal">{feature}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Warranty Period */}
                  <div className="space-y-2">
                    <Label>Warranty Period (Years)</Label>
                    <NumericField
                      placeholder="e.g., 10"
                      value={newDoorGlassType.warrantyYears}
                      onChange={(v) => onFormChange({ ...newDoorGlassType, warrantyYears: v })}
                      rules={{ integer: true, gtZero: true, label: 'Warranty' }}
                      showError={submitAttempted}
                      data-testid="door-glass-warranty-input"
                      errorTestId="door-glass-warranty-error"
                    />
                  </div>
                  
                  {/* Grid Checkbox */}
                  <div className="space-y-2 border-t pt-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <Checkbox
                        checked={newDoorGlassType.hasGrid || false}
                        onCheckedChange={(isChecked) => {
                          onFormChange({
                            ...newDoorGlassType,
                            hasGrid: isChecked,
                            gridSize: isChecked ? newDoorGlassType.gridSize : null
                          });
                          if (isChecked) {
                            setTimeout(() => doorGridSizeRef?.current?.focus(), 100);
                          }
                        }}
                      />
                      <span>This glass type has grid</span>
                    </label>
                  </div>
                  
                  {newDoorGlassType.hasGrid && (
                    <div className="space-y-2">
                      <Label>Grid Size (in) *</Label>
                      <NumericField
                        placeholder="e.g., 6"
                        value={newDoorGlassType.gridSize}
                        onChange={(v) => onFormChange({ ...newDoorGlassType, gridSize: v })}
                        rules={{ required: true, min: 0, allowFraction: true, label: 'Grid size' }}
                        showError={submitAttempted}
                        required
                        data-testid="door-glass-grid-size-input"
                        errorTestId="door-glass-grid-size-error"
                      />
                    </div>
                  )}
                  
                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => onDialogOpenChange(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={operationLoading}>
                      {operationLoading ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          {editingDoorGlassType ? 'Updating...' : 'Creating...'}
                        </>
                      ) : (
                        <>{editingDoorGlassType ? 'Update' : 'Create'} Glass Type</>
                      )}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {filteredDoorGlassTypes.length === 0 ? (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed rounded-lg">
          <p>No glass types created yet</p>
        </div>
      ) : (
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full text-sm table-fixed">
            <thead className="bg-slate-100">
              <tr className="border-b">
                {canEditCSIItems && (
                  <th className="w-12 px-3 py-3 text-left">
                    <Checkbox
                      checked={selectedDoorGlassTypeIds.length === filteredDoorGlassTypes.length && filteredDoorGlassTypes.length > 0}
                      onCheckedChange={onSelectAll}
                    />
                  </th>
                )}
                <th className="w-24 px-3 py-3 text-left font-semibold text-slate-700">Type</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Manufacturer</th>
                <th className="w-36 px-3 py-3 text-left font-semibold text-slate-700">Model</th>
                <th className="px-3 py-3 text-left font-semibold text-slate-700">Build</th>
                {canEditCSIItems && (
                  <th className="w-28 px-3 py-3 text-right font-semibold text-slate-700">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {filteredDoorGlassTypes.map((glassType, idx) => (
                <React.Fragment key={glassType.id}>
                  <tr 
                    className={`border-b hover:bg-slate-50 transition-colors ${
                      selectedDoorGlassTypeIds.includes(glassType.id) ? 'bg-blue-50' : idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'
                    }`}
                  >
                    {canEditCSIItems && (
                      <td className="w-12 px-3 py-2">
                        <Checkbox
                          checked={selectedDoorGlassTypeIds.includes(glassType.id)}
                          onCheckedChange={(checked) => onToggleSelection(glassType.id, { target: { checked } })}
                        />
                      </td>
                    )}
                    <td className="w-24 px-3 py-2 font-medium text-slate-900">{glassType.name}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{glassType.manufacturer || '-'}</td>
                    <td className="w-36 px-3 py-2 text-slate-600 truncate">{glassType.model || '-'}</td>
                    <td className="px-3 py-2 text-slate-600 truncate">{glassType.glassBuild || '-'}</td>
                    {canEditCSIItems && (
                      <td className="w-28 px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className={`h-7 w-7 p-0 transition-colors ${
                              expandedDoorGlassTypeIds?.includes(glassType.id) 
                                ? 'bg-orange-500 text-white hover:bg-orange-600' 
                                : 'text-slate-500 hover:bg-orange-500 hover:text-white'
                            }`}
                            onClick={() => onToggleExpand(glassType.id)}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(glassType)}>
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:bg-red-600 hover:text-white" onClick={() => onDelete(glassType)}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </td>
                    )}
                  </tr>
                  {/* Expanded Details Row */}
                  {expandedDoorGlassTypeIds?.includes(glassType.id) && (
                    <tr className="bg-slate-50 border-b">
                      {canEditCSIItems && <td className="w-12"></td>}
                      <td colSpan={canEditCSIItems ? 5 : 4} className="px-3 py-3">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="font-semibold text-slate-700">Thickness:</span>
                            <span className="ml-2 text-slate-600">{glassType.glassThickness ? `${glassType.glassThickness}"` : '-'}</span>
                          </div>
                          {glassType.hasGrid && (
                            <div>
                              <span className="font-semibold text-slate-700">Grid Size:</span>
                              <span className="ml-2 text-slate-600">{glassType.gridSize ? `${glassType.gridSize}"` : 'Yes'}</span>
                            </div>
                          )}
                          {glassType.warrantyYears && (
                            <div>
                              <span className="font-semibold text-slate-700">Warranty:</span>
                              <span className="ml-2 text-slate-600">{glassType.warrantyYears} Years</span>
                            </div>
                          )}
                          {glassType.features && glassType.features.length > 0 && (
                            <div className="col-span-2 md:col-span-4 mt-1">
                              <span className="font-semibold text-slate-700">Features:</span>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {glassType.features.map((feature, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-slate-200 rounded text-xs">{feature}</span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DoorGlassTypesSection;
